import React from 'react'
import { useCallback, useEffect, useState, useRef, FC } from 'react'
import EventCell from '../EventCell'
import styles from './style.module.scss'
import MonthBoxCell, { MonthBoxCellIdPrefix } from '../MonthBoxCell'
import * as calendarService from '../../modules/CalendarService'
import { connect, ConnectedProps } from 'react-redux'
import moment from 'moment'
import * as dateUtils from '../../utils/DateUtils'
import DayEventList from '../DayEventList'
import { t } from 'i18next'

import {
  getTodoInstances,
  addTodoChangeListener,
  removeTodoChangeListener,
} from '../../modules/TodoUtils'

interface RootState {
  settings: any
}

interface MonthBoxOwnProps {
  browseDate: Date
  focusedDate: Date
  onFocusClick: (date: Date) => void
  onEditEvent: (
    date: Date,
    instanceId?: string | null,
    callback?: (() => void) | null,
    targetDates?: any,
    weekly?: boolean,
    isAllday?: boolean,
    onCancel?: () => void,
  ) => void
  onEventClick: (event: any, cellElement: HTMLDivElement | null) => void
  onEventDoubleClick: (event: any, cellElement: HTMLDivElement | null) => void
}

const mapStateToProps = (state: RootState) => ({
  settings: state.settings,
})

const connector = connect(mapStateToProps)
type PropsFromRedux = ConnectedProps<typeof connector>
type MonthBoxProps = PropsFromRedux & MonthBoxOwnProps

const EVENT_CELL_HEIGHT = 20
const GAP_BETWEEN_EVENT_CELL = 2
const MONTH_CELL_DATE_LABEL_HEIGHT =
  4 +
  24 +
  GAP_BETWEEN_EVENT_CELL /* 4 is TOP MARGIN, 24 is DATE LABEL HEIGHT, 2 is GAP BETWEEN EVENT CELL */
const WEEK_NUMBER_COLUMN_WIDTH = 32

const MonthBox: FC<MonthBoxProps> = ({
  browseDate,
  focusedDate,
  onFocusClick,
  onEditEvent,
  onEventClick,
  onEventDoubleClick,
  settings,
}) => {
  const showWeekNumber = settings.calendar.showWeekNumber
  const firstWeekday = settings.calendar.firstdayOfWeek
  const timeFormat = settings.general?.timeFormat

  let monthFirstDate = dateUtils.getMonthViewFirstDate(browseDate)
  let monthLastDate = dateUtils.getMonthViewLastDate(browseDate)
  const monthViewDayCount = dateUtils.betweenDay(monthFirstDate, monthLastDate) + 1
  const monthViewWeekCount = monthViewDayCount / 7

  if (
    !(
      monthFirstDate.getTime() <= focusedDate.getTime() &&
      monthLastDate.getTime() >= focusedDate.getTime()
    )
  ) {
    browseDate = focusedDate
    monthFirstDate = dateUtils.getMonthViewFirstDate(focusedDate)
    monthLastDate = dateUtils.getMonthViewLastDate(focusedDate)
  }

  const [faceDate, setFaceDate] = useState(browseDate)
  const [eventCells, setEventCells] = useState([])
  const [selectedDate, setSelectedDate] = useState(null)
  const [overlayPosition, setOverlayPosition] = useState(null)
  const [listHeight, setListHeight] = useState(0)
  const dayEventsRef = useRef(null)
  const applyFirstWeek = useRef(firstWeekday)
  const applyShowWeekNumber = useRef(showWeekNumber)
  const applyTimeFormat = useRef(timeFormat)
  const monthBoxRef = useRef(null)
  const resizeObserverRef = useRef(null)
  const resizeTimerRef = useRef(null)

  if (
    applyFirstWeek.current !== firstWeekday ||
    applyShowWeekNumber.current !== showWeekNumber ||
    applyTimeFormat.current !== timeFormat
  ) {
    // 첫주 요일 변경 후 이벤트 뷰 재계산
    setTimeout(() => {
      _calculateEventViews()
    }, 100)
    applyFirstWeek.current = firstWeekday
    applyShowWeekNumber.current = showWeekNumber
    applyTimeFormat.current = timeFormat
  }

  if (
    faceDate.getFullYear() !== browseDate.getFullYear() ||
    faceDate.getMonth() !== browseDate.getMonth()
  ) {
    setFaceDate(browseDate)
  }

  let loopDate = new Date(monthFirstDate)

  let dates = []
  for (let i = 0; i < monthViewDayCount; i++) {
    dates.push(loopDate)
    loopDate = dateUtils.dateAddingDate(loopDate)
  }

  const weekdaysShort = moment.weekdaysShort()
  let weekdays = []
  for (let i = 0; i < 7; i++) {
    let color = 'rgb(38, 38, 38)'
    const weekday = dates[i].getDay()
    if (weekday === 0) {
      color = 'rgb(253, 76, 69)'
    } else if (weekday === 6) {
      color = 'rgb(0, 122, 255)'
    }
    weekdays.push({ title: weekdaysShort[weekday].toUpperCase(), color: color })
  }

  const _calculateEventViews = useCallback(async () => {
    const monthBoxElement = monthBoxRef.current
    // 언마운트되었거나 DOM에서 분리된(detached) 인스턴스는 계산/렌더를 건너뛴다.
    // 라우터 전환 등으로 남은 유령 인스턴스가 emit마다 setEventCells를 호출해
    // 실제 보이는 인스턴스의 렌더를 방해하는 것을 방지한다.
    if (monthBoxElement == null || !monthBoxElement.isConnected) {
      return
    }

    let currentDate = faceDate
    let monthFirstDate = dateUtils.getMonthViewFirstDate(currentDate)
    let monthLastDate = dateUtils.getMonthViewLastDate(currentDate)
    const monthLastTime = dateUtils.dateAddingDate(monthLastDate, 1)

    let instances = calendarService.getInstances()
    // filter out instances that are not in the month
    instances = instances.filter((instance) => {
      if (
        instance.startDate.getTime() > monthLastTime.getTime() ||
        instance.endDate.getTime() <= monthFirstDate.getTime()
      ) {
        return false
      }
      return true
    })

    const todoInstances = await getTodoInstances(monthFirstDate, monthLastTime)
    if (todoInstances.length > 0) {
      instances.push(...todoInstances)
      const locale = settings.general?.locale
      instances.sort((a, b) => {
        if (a.startDate.getTime() !== b.startDate.getTime()) {
          return a.startDate.getTime() - b.startDate.getTime()
        }

        if (a.todo && !b.todo) {
          return 1
        }
        if (b.todo && !a.todo) {
          return 1
        }
        if (a.todo?.status !== b.todo?.status) {
          return a.todo?.status === 'COMPLETED' ? 1 : -1
        }
        const aSubject = a.subject || ''
        const bSubject = b.subject || ''
        return aSubject.localeCompare(bSubject, locale)
      })
    }

    let tempMaxCount = 4
    const month = monthFirstDate.getMonth() + 1
    const childId = MonthBoxCellIdPrefix + month + '_' + monthFirstDate.getDate()
    const matchChild = document.querySelector('#' + childId)
    let parentRect = null
    if (matchChild !== null) {
      const rect = matchChild.getBoundingClientRect()
      const height = rect.height - MONTH_CELL_DATE_LABEL_HEIGHT
      tempMaxCount = Math.floor(height / (EVENT_CELL_HEIGHT + GAP_BETWEEN_EVENT_CELL))
      parentRect = matchChild.parentElement.getBoundingClientRect()
    }
    const maxCount = tempMaxCount

    // monthFirstDate 부터 monthViewWeekCount의 주까지 시작 시간과 끝 시간을 구해서 각각 포함되는 인스턴스를 구함
    let weekInfoArr = []
    let weekFirstDate = monthFirstDate
    let remainInstances = instances
    for (let i = 0; i < monthViewWeekCount; i++) {
      let weekLastDate = dateUtils.dateAddingDate(dateUtils.getWeekLastDate(weekFirstDate), 1)

      let weekInstances = []
      let willRemoveIndexes = []

      const _weekFirstDate = weekFirstDate
      remainInstances.forEach((instance, index) => {
        if (
          instance.startDate.getTime() >= weekLastDate.getTime() ||
          instance.endDate.getTime() < _weekFirstDate.getTime()
        ) {
          return
        }
        weekInstances.push(instance)
        if (instance.endDate.getTime() <= weekLastDate.getTime()) {
          willRemoveIndexes.push(index)
        }
      })

      willRemoveIndexes.reverse().forEach((index) => {
        remainInstances.splice(index, 1)
      })

      weekInfoArr.push({
        instances: weekInstances,
        startDate: weekFirstDate,
        endDate: weekLastDate,
      })

      weekFirstDate = dateUtils.getDateZero(weekLastDate)
    }

    let dayInfoArr = []
    weekInfoArr.forEach((weekInfo, _weekIndex) => {
      const _weekFirstDate = weekInfo.startDate
      const _weekLastDate = weekInfo.endDate
      let remainPosY = {}
      let weekInstances = weekInfo.instances.slice()
      weekInstances.sort((a, b) => {
        const aValue =
          a.startDate.getFullYear() * 10000 + a.startDate.getMonth() * 100 + a.startDate.getDate()
        const bValue =
          b.startDate.getFullYear() * 10000 + b.startDate.getMonth() * 100 + b.startDate.getDate()
        if (aValue !== bValue) {
          return aValue - bValue
        }

        let AisAllDay = a.allday
        if (!AisAllDay && a.startDate.getDate() !== a.endDate.getDate()) {
          AisAllDay = true
        }
        let BisAllDay = b.allday
        if (!BisAllDay && b.startDate.getDate() !== b.endDate.getDate()) {
          BisAllDay = true
        }
        if (AisAllDay && BisAllDay) {
          return b.endDate.getTime() - a.endDate.getTime()
        }
        if (!AisAllDay && !BisAllDay) {
          return a.startDate.getTime() - b.startDate.getTime()
        }
        if (AisAllDay) {
          return -1
        }
        return 1
      })

      for (let dayIndex = 0; dayIndex < 7; dayIndex++) {
        let dayInstances = []
        const dayDate = dateUtils.dateAddingDate(_weekFirstDate, dayIndex)
        const dayLastDate = dateUtils.dateAddingDate(dayDate, 1)
        for (let instanceIndex = 0; instanceIndex < weekInstances.length; instanceIndex++) {
          const instance = weekInstances[instanceIndex]
          if (instance.startDate.getTime() < dayLastDate.getTime()) {
            while (remainPosY[dayInstances.length]) {
              dayInstances.push({ isEmpty: true })
            }
            dayInstances.push(instance)
            weekInstances.splice(instanceIndex, 1)
            instanceIndex--
          } else {
            break // 인스탄스가 다음날 현 시간에 포함되지 않음 다음 날.
          }

          if (instance.endDate.getTime() > dayLastDate.getTime()) {
            remainPosY[dayInstances.length - 1] = instance
          }
        }

        // max Index
        const maxIndex = Object.keys(remainPosY).reduce((acc, key) => {
          const index = parseInt(key)
          if (index >= dayInstances.length) {
            acc = index
          }
          return acc
        }, -1)
        for (let i = dayInstances.length; i < maxIndex + 1; i++) {
          if (remainPosY[i]) {
            dayInstances.push({ isEmpty: true })
          } else {
            dayInstances.push(null)
          }
        }

        dayInfoArr.push({
          instances: dayInstances,
          dayDate: dayDate,
          weekFirstDate: _weekFirstDate,
          weekLastDate: _weekLastDate,
        })

        const tempRemainPosY = remainPosY
        remainPosY = Object.keys(remainPosY).reduce((acc, key) => {
          const remainInstance = tempRemainPosY[key]
          if (remainInstance.endDate.getTime() > dayLastDate.getTime()) {
            acc[key] = remainInstance
          }
          return acc
        }, {})
      }
    })

    let inEventsCells = []

    const maxShowCount = maxCount - 1
    dayInfoArr.forEach((dayInfo) => {
      let overCount = 0

      const month = dayInfo.dayDate.getMonth() + 1
      const childId = MonthBoxCellIdPrefix + month + '_' + dayInfo.dayDate.getDate()
      const matchChild = document.querySelector('#' + childId)
      if (matchChild === null) {
        return null
      }
      const rect = matchChild.getBoundingClientRect()

      for (let i = 0; i < dayInfo.instances.length; i++) {
        const instance = dayInfo.instances[i]
        if (i >= maxShowCount) {
          if (instance !== null) {
            overCount++
          }
          continue
        }

        if (instance === null) {
          continue
        }
        if (instance.isEmpty) {
          continue
        }

        let allday = instance.allday
        let alldayOffset = 0
        if (!allday) {
          const startDate = moment(instance.startDate)
          const endDate = moment(instance.endDate.getTime() - 1000)
          allday = !startDate.isSame(endDate, 'day')
          alldayOffset = -1000
        } else if (instance.startDate.valueOf() !== instance.endDate.valueOf()) {
          alldayOffset = -1000
        }

        let instanceStartDate = instance.startDate
        if (instanceStartDate.getTime() < dayInfo.weekFirstDate.getTime()) {
          instanceStartDate = dayInfo.weekFirstDate
        }

        let instanceEndDate = new Date(instance.endDate.getTime() + alldayOffset)
        if (instanceEndDate.getTime() >= dayInfo.weekLastDate.getTime()) {
          instanceEndDate = new Date(dayInfo.weekLastDate.getTime() + alldayOffset)
        }

        const dayLen = dateUtils.betweenDay(instanceStartDate, instanceEndDate) + 1
        const topRect = monthBoxElement.getBoundingClientRect()

        const rectStyle = {
          left: rect.x + 2 - topRect.left,
          top:
            rect.y -
            topRect.top +
            MONTH_CELL_DATE_LABEL_HEIGHT +
            i * (EVENT_CELL_HEIGHT + GAP_BETWEEN_EVENT_CELL),
          width: rect.width * dayLen - 4, //margin LR 2 * 2
          height: EVENT_CELL_HEIGHT,
        }

        if (parentRect != null) {
          const weekNoWidth = applyShowWeekNumber.current ? WEEK_NUMBER_COLUMN_WIDTH : 0
          const pWidth = parentRect.width + weekNoWidth
          ;(rectStyle as any).left = (rectStyle.left * 100) / pWidth + '%'
          ;(rectStyle as any).width = (rectStyle.width * 100) / pWidth + '%'
          // rectStyle.top = (rectStyle.top * 100) / parentRect.height + '%'
          // rectStyle.height = (rectStyle.height * 100) / parentRect.height + '%'
        }

        let style: any = {
          position: 'absolute',
          ...rectStyle,
        }

        let title = instance.subject
        if (title == null || title.length === 0) {
          title = t('editor.value.noSubject')
        }
        if (allday) {
          if (instance.todo == null) {
            title = (instance.isCanceled ? 'Canceled: ' : '') + title
          }
        } else {
          title =
            dateUtils.localizedTimeStringRemoveMinute(instance.startDate) +
            ' ' +
            (instance.isCanceled ? 'Canceled: ' : '') +
            title
        }

        const eventCell = {
          style: style,
          title: title,
          key: childId + '_' + instance.id,
          isOverCount: false,
          isFree: !instance.isBusy,
          instanceId: instance.id,
          endDate: instance.endDate,
          date: dayInfo.dayDate,
          allday: allday,
          isNotResponse: instance.isMeeting && instance.myMeetingResponse === null,
          isMyEvent: instance.isMyEvent,
          color: instance.color,
          folderColor: instance.folderColor,
          isCanceled: instance.isCanceled,
          isDeclined: instance.isDeclined,
          instance: instance,
          backgroundColor: instance.color + (allday ? 'ff' : '56'), // 55 is 0.35 alpha
          useDragImage: true,
          todo: instance.todo,
          isTodo: instance.todo != null,
        }

        inEventsCells.push(eventCell)
      }
      if (overCount > 0) {
        const topRect = monthBoxElement.getBoundingClientRect()
        let style = {
          position: 'absolute',
          left: rect.x + 2 - topRect.left,
          top:
            maxShowCount * (EVENT_CELL_HEIGHT + GAP_BETWEEN_EVENT_CELL) +
            rect.y -
            topRect.top +
            MONTH_CELL_DATE_LABEL_HEIGHT,
          width: rect.width - 4, //margin LR 2 * 2
          height: EVENT_CELL_HEIGHT,
          justifyContent: 'center',
        }
        if (parentRect != null) {
          const weekNoWidth = applyShowWeekNumber.current ? WEEK_NUMBER_COLUMN_WIDTH : 0
          const pWidth = parentRect.width + weekNoWidth
          ;(style as any).left = (style.left * 100) / pWidth + '%'
          ;(style as any).width = (style.width * 100) / pWidth + '%'
          // style.top = (style.top * 100) / parentRect.height + '%'
          // style.height = (style.height * 100) / parentRect.height + '%'
        }
        const eventCell = {
          color: 'lightgray',
          style: style,
          title: '+' + overCount,
          key: childId + '_' + 'overCount',
          isOverCount: true,
          date: dayInfo.dayDate,
          overCountId: 'over-count-' + moment(dayInfo.dayDate).format('YYYY-MM-DD'),
        }
        inEventsCells.push(eventCell)
      }
    })

    // await(getTodoInstances) 사이에 언마운트/detach된 경우, 죽은 인스턴스가
    // setEventCells를 호출하지 않도록 한 번 더 확인한다.
    if (!monthBoxRef.current?.isConnected) {
      return
    }
    setEventCells(inEventsCells)
  }, [faceDate])

  useEffect(() => {
    if (monthBoxRef.current) {
      const resizeObj: any = {
        resizeCalled: false,
        timer: null,
      }

      resizeTimerRef.current = resizeObj

      resizeObj.resizeCallback = () => {
        if (resizeObj.timer != null) {
          // cancelAnimationFrame(resizeTimerRef.current)
          resizeObj.resizeCalled = true
          return
        }
        _calculateEventViews()
        resizeObj.resizeCalled = false
        resizeObj.timer = setTimeout(() => {
          resizeObj.timer = null
          if (resizeObj.resizeCalled === true) {
            resizeObj.resizeCallback()
            // isMenuToggled()
          }
        }, 100)
      }

      resizeObserverRef.current = new ResizeObserver(() => {
        resizeTimerRef.current.resizeCallback()
      })

      resizeObserverRef.current.observe(monthBoxRef.current)
    }

    _calculateEventViews()
    calendarService.addInstanceListener(_calculateEventViews)
    addTodoChangeListener(_calculateEventViews)

    return () => {
      if (resizeObserverRef.current) {
        resizeObserverRef.current.disconnect()
      }
      if (resizeTimerRef.current != null) {
        clearTimeout(resizeTimerRef.current.timer)
      }
      calendarService.removeInstanceListener(_calculateEventViews)
      removeTodoChangeListener(_calculateEventViews)
    }
  }, [_calculateEventViews])

  //DayEventList [
  const onDayEventsDidRender = (dayEventListElement) => {
    if (dayEventListElement) {
      const height = dayEventListElement.clientHeight
      setListHeight(height)
    }
  }

  useEffect(() => {
    const inFunc = () => {
      if (listHeight > 0 && overlayPosition) {
        const monthBoxElement = document.querySelector(`.${styles.monthBox}`)
        const monthBoxRect = monthBoxElement.getBoundingClientRect()

        const overlayTop = overlayPosition.top
        const inListHeight = dayEventsRef.current.clientHeight

        let offset = 0
        // 아래쪽 경계를 벗어나는지 확인
        if (overlayTop + inListHeight > monthBoxRect.bottom) {
          offset = overlayTop + inListHeight - monthBoxRect.bottom + 25
        }

        let newTop = overlayPosition.top - offset
        // 위쪽 경계를 벗어나는지 확인
        if (newTop < 20) {
          newTop = 20
        }

        let newHeight =
          overlayPosition.height > inListHeight ? overlayPosition.height : inListHeight
        let forceHeight = 0

        if (newTop + newHeight > monthBoxRect.bottom - 25) {
          newHeight = monthBoxRect.bottom - 25 - newTop
          forceHeight = newHeight
        }

        setOverlayPosition((prev) => ({
          ...prev,
          top: newTop,
          height: newHeight,
          forceHeight: forceHeight,
        }))
      }
    }
    setTimeout(inFunc, 10)
  }, [listHeight])

  const handleOverlayClose = () => {
    setSelectedDate(null)
    setOverlayPosition(null)
    setListHeight(0)
    dayEventsRef.current = null
  }

  const showDayEvents = (date, cellElement) => {
    const rect = cellElement.getBoundingClientRect()
    const monthBoxElement = document.querySelector(`.${styles.monthBox}`)
    const monthBoxRect = monthBoxElement.getBoundingClientRect()

    // 기본 위치와 크기 계산
    const width = rect.width + 40
    let left = rect.left - 20

    // 오른쪽 경계를 벗어나는지 확인
    if (left + width > monthBoxRect.right) {
      left = monthBoxRect.right - width
    }
    // 왼쪽 경계를 벗어나는지 확인
    if (left < monthBoxRect.left) {
      left = monthBoxRect.left
    }

    setOverlayPosition({
      top: rect.top,
      left: left,
      width: width,
      height: rect.height,
      forceHeight: 0,
    })
    setSelectedDate(date)
  }
  //DayEventList ]

  // 이벤트 클릭 핸들러
  const handleEventClick = (event, cellElement) => {
    if (onEventClick) {
      onEventClick(event, cellElement)
    }
  }

  const initialDraggingEventDate = {
    instanceId: null,
    start: null,
    end: null,
    duration: null,
    proceedPos: -1,
  }
  const draggingEventDate = useRef(initialDraggingEventDate)
  const mainContentRef = useRef(null)
  const [dragEventAreaCells, setDragEventAreaCells] = useState([])

  const handleDragStart = (e) => {
    const instanceId = e.dataTransfer.getData('data')
    const instance = calendarService.getInstance(instanceId)
    if (!instance) {
      console.log('drag start instance not found', instanceId)
      draggingEventDate.current = initialDraggingEventDate
      return
    }

    let durationDay = 1
    if (instance.allday) {
      durationDay = dateUtils.betweenDay(instance.startDate, instance.endDate)
      if (durationDay === 0) {
        durationDay = 1
      }
    }
    draggingEventDate.current = {
      instanceId: instanceId,
      start: instance.startDate,
      end: instance.endDate,
      duration: durationDay,
      durationDay,
      proceedPos: 0,
    } as any
    console.log('MonthBox handleDragStart', draggingEventDate.current)
  }

  const handleDragMove = (e) => {
    // console.log('MonthBox handleDragMove', e)
    // TODO: MonthBoxCell 위로 드래그 중인 Event가 차지할 영역을 표시.
    if (draggingEventDate.current.start === null) {
      return
    }

    const mainContentRect = mainContentRef.current.getBoundingClientRect()
    const x = e.clientX - mainContentRect.left
    const y = e.clientY - mainContentRect.top - 31

    const monthCellX = Math.floor(x / (mainContentRect.width / 7))
    const monthCellY = Math.floor(y / ((mainContentRect.height - 32) / monthViewWeekCount)) // 32 is weekNumberColumn height
    const monthCellIndex = monthCellX + monthCellY * 7
    if (monthCellIndex < 0 || monthCellIndex >= monthViewDayCount) {
      console.log('out of range', monthCellIndex)
      return
    }

    if (monthCellIndex === draggingEventDate.current.proceedPos) {
      return
    }
    draggingEventDate.current.proceedPos = monthCellIndex
    console.log('MonthBox handleDragMove', mainContentRect, monthCellIndex)

    let index = 0
    const areaCells = []
    const endIndex = monthCellIndex + (draggingEventDate.current as any).durationDay
    for (let entry of mainContentRef.current.childNodes.entries()) {
      const child = entry[1]
      if (child.id.startsWith(MonthBoxCellIdPrefix)) {
        if (index >= monthCellIndex && index < endIndex) {
          areaCells.push(child)
        }
        if (index >= endIndex) {
          break
        }
        index++
      }
    }
    setDragEventAreaCells(areaCells)
  }

  const handleDragEnd = (e) => {
    console.log('MonthBox handleDragEnd', e)
    draggingEventDate.current = initialDraggingEventDate
    setDragEventAreaCells([])
  }

  const handleDrop = (e) => {
    console.log('MonthBox handleDrop', e)

    const instance = calendarService.getInstance(draggingEventDate.current.instanceId)

    if (instance) {
      const id = dragEventAreaCells[0].id
      const month = parseInt(id.split('_')[1])
      const day = parseInt(id.split('_')[2])
      let startDate = null
      let endDate = null
      if (instance.allday) {
        startDate = new Date(faceDate.getFullYear(), month - 1, day)
        endDate = new Date(
          faceDate.getFullYear(),
          month - 1,
          day + (draggingEventDate.current as any).durationDay + 1,
        )
      } else {
        startDate = new Date(
          faceDate.getFullYear(),
          month - 1,
          day,
          instance.startDate.getHours(),
          instance.startDate.getMinutes(),
        )
        endDate = new Date(
          faceDate.getFullYear(),
          month - 1,
          day,
          instance.endDate.getHours(),
          instance.endDate.getMinutes(),
        )
      }
      calendarService.updateEventChangeTime(instance, startDate, endDate, instance.allday)
    }

    draggingEventDate.current = initialDraggingEventDate
    setDragEventAreaCells([])
  }

  return (
    <div className={styles.monthBox} ref={monthBoxRef}>
      {showWeekNumber && (
        <div className={styles.weekNumberColumn}>
          {[...Array(monthViewWeekCount)].map((_, index) => (
            <div
              key={index}
              className={styles.weekNumber}
              style={{ '--var-week-count': monthViewWeekCount } as any}
            >
              {dateUtils.getWeekNumber(dates[index * 7])}
            </div>
          ))}
        </div>
      )}
      <div
        className={styles.mainContent}
        ref={mainContentRef}
        onDragStart={handleDragStart}
        onDrag={handleDragMove}
        onDragEnd={handleDragEnd}
        onDragOver={(e) => {
          //console.log('MonthBox handleDragOver', e)
          //onDrop 동작을 위해서 필요.
          e.preventDefault()
        }}
        onDrop={handleDrop}
      >
        <div className={styles.weekdayRow}>
          {weekdays.map((day, index) => (
            <div
              key={index}
              className={styles.weekday}
              style={{ color: day.color, fontWeight: 'bold' }}
            >
              {day.title}
            </div>
          ))}
        </div>
        {dates.map((date, _index) => {
          return (
            <MonthBoxCell
              date={date}
              key={date.getMonth() + '.' + date.getDate()}
              isFocused={moment(date).isSame(focusedDate, 'day')}
              isCurrentMonth={moment(date).isSame(faceDate, 'month')}
              onCellClick={(date) => {
                console.log('MonthBoxCell click %o', date)
                onFocusClick(date)
              }}
              onCellDoubleClick={(date) => {
                console.log('MonthBoxCell double click %o', date)
                onEditEvent(date)
              }}
              weekCount={monthViewWeekCount}
            />
          )
        })}
        {eventCells.map((e) => {
          return (
            <EventCell
              overCountId={e.overCountId}
              event={e}
              key={e.key}
              onCellClick={(e, cellElement) => {
                if (e.instanceId) {
                  handleEventClick(e, cellElement)
                } else {
                  const cellId = `${MonthBoxCellIdPrefix}${(e as any).date.getMonth() + 1}_${(e as any).date.getDate()}`
                  const cell = document.getElementById(cellId)
                  if (cell) {
                    showDayEvents((e as any).date, cell)
                  }
                }
              }}
              onCellDoubleClick={onEventDoubleClick}
            />
          )
        })}
        {dragEventAreaCells.map((cell) => {
          const cellRect = cell.getBoundingClientRect()
          const styleDict = {
            top: cellRect.top,
            left: cellRect.left,
            width: cellRect.width,
            height: cellRect.height,
          }
          return <div key={cell.id} className={styles.dragEventArea} style={styleDict} />
        })}

        {selectedDate && overlayPosition && (
          <>
            <div className={styles.overlay} onClick={handleOverlayClose} />
            <div
              className={styles.dayEventsOverlay}
              style={{
                top: overlayPosition.top,
                left: overlayPosition.left,
                width: overlayPosition.width,
                height: overlayPosition.height,
              }}
            >
              <div
                ref={dayEventsRef}
                style={{
                  width: overlayPosition.width,
                  height: overlayPosition.forceHeight > 0 ? overlayPosition.forceHeight : null,
                }}
              >
                <DayEventList
                  date={selectedDate}
                  onClose={handleOverlayClose}
                  onDidRender={onDayEventsDidRender}
                  onCellClick={(e, cellElement) => {
                    handleEventClick(e, cellElement)
                  }}
                  onCellDoubleClick={onEventDoubleClick}
                />
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  )
}

export default connector(MonthBox)
